import {
  numberAnswerOutOfRange,
  parseFormNumberAnswer,
} from "@/modules/forms/domain/lib/form-number-answer";
import type {
  FormFieldOptionItem,
  RepeaterCountMode,
  RepeaterItemFieldDef,
  RepeaterSettings,
  RepeaterSubFieldType,
} from "@/server/platform/db/schema/forms";
import {
  repeaterCountModes,
  repeaterSubFieldTypes,
} from "@/server/platform/db/schema/forms";

export type {
  RepeaterCountMode,
  RepeaterItemFieldDef,
  RepeaterSettings,
  RepeaterSubFieldType,
};
export { repeaterCountModes, repeaterSubFieldTypes };

export type RepeaterItemRow = Record<string, string>;
export type RepeaterAnswer = RepeaterItemRow[];

const DEFAULT_ITEM_LABEL = "ລາຍການ #{index}";

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function parseOptionItems(raw: unknown): FormFieldOptionItem[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: FormFieldOptionItem[] = [];
  for (const row of raw) {
    if (!isRecord(row)) continue;
    const value = typeof row.value === "string" ? row.value : "";
    const label = typeof row.label === "string" ? row.label : "";
    if (!value.trim() || !label.trim()) continue;
    out.push({
      value: value.trim(),
      label: label.trim(),
      enabled: row.enabled === false ? false : undefined,
    });
  }
  return out.length > 0 ? out : undefined;
}

function parseItemFieldDef(raw: unknown): RepeaterItemFieldDef | null {
  if (!isRecord(raw)) return null;
  const key = typeof raw.key === "string" ? raw.key.trim() : "";
  const label = typeof raw.label === "string" ? raw.label.trim() : "";
  const type = raw.type;
  if (
    !key ||
    !label ||
    typeof type !== "string" ||
    !repeaterSubFieldTypes.includes(type as RepeaterSubFieldType)
  ) {
    return null;
  }
  const def: RepeaterItemFieldDef = {
    key,
    label,
    type: type as RepeaterSubFieldType,
  };
  if (raw.required === true) def.required = true;
  if (typeof raw.placeholder === "string" && raw.placeholder.trim()) {
    def.placeholder = raw.placeholder.trim();
  }
  const min =
    typeof raw.min === "number" && Number.isFinite(raw.min) ? raw.min : undefined;
  const max =
    typeof raw.max === "number" && Number.isFinite(raw.max) ? raw.max : undefined;
  if (min !== undefined) def.min = min;
  if (max !== undefined) def.max = max;
  if (typeof raw.minLength === "number" && Number.isFinite(raw.minLength)) {
    def.minLength = raw.minLength;
  }
  if (typeof raw.maxLength === "number" && Number.isFinite(raw.maxLength)) {
    def.maxLength = raw.maxLength;
  }
  if (raw.integerOnly === true) def.integerOnly = true;
  if (typeof raw.pattern === "string" && raw.pattern.trim()) {
    def.pattern = raw.pattern.trim();
  }
  const options = parseOptionItems(raw.options);
  if (options) def.options = options;
  return def;
}

export function parseRepeaterSettings(raw: unknown): RepeaterSettings | null {
  if (!isRecord(raw)) return null;
  const countMode = raw.countMode;
  if (
    typeof countMode !== "string" ||
    !repeaterCountModes.includes(countMode as RepeaterCountMode)
  ) {
    return null;
  }
  if (!Array.isArray(raw.itemFields) || raw.itemFields.length === 0) {
    return null;
  }
  const itemFields: RepeaterItemFieldDef[] = [];
  for (const row of raw.itemFields) {
    const parsed = parseItemFieldDef(row);
    if (parsed) itemFields.push(parsed);
  }
  if (itemFields.length === 0) return null;

  const settings: RepeaterSettings = {
    countMode: countMode as RepeaterCountMode,
    itemFields,
  };

  if (typeof raw.fixedCount === "number" && Number.isFinite(raw.fixedCount)) {
    settings.fixedCount = Math.trunc(raw.fixedCount);
  }
  if (typeof raw.minItems === "number" && Number.isFinite(raw.minItems)) {
    settings.minItems = Math.trunc(raw.minItems);
  }
  if (typeof raw.maxItems === "number" && Number.isFinite(raw.maxItems)) {
    settings.maxItems = Math.trunc(raw.maxItems);
  }
  if (
    typeof raw.itemLabelTemplate === "string" &&
    raw.itemLabelTemplate.trim()
  ) {
    settings.itemLabelTemplate = raw.itemLabelTemplate.trim();
  }
  if (raw.allowAddRemove === false) settings.allowAddRemove = false;
  if (raw.showItemNumber === false) settings.showItemNumber = false;
  return settings;
}

export function repeaterSettingsFromFieldSettings(
  raw: Record<string, unknown> | null | undefined,
): RepeaterSettings | null {
  return parseRepeaterSettings(raw);
}

export function defaultRepeaterSettings(): RepeaterSettings {
  return {
    countMode: "dynamic",
    minItems: 1,
    maxItems: 10,
    allowAddRemove: true,
    showItemNumber: true,
    itemLabelTemplate: DEFAULT_ITEM_LABEL,
    itemFields: [
      {
        key: "name",
        label: "ຊື່",
        type: "text",
        required: true,
      },
    ],
  };
}

export function repeaterSettingsToRecord(
  settings: RepeaterSettings,
): Record<string, unknown> {
  return { ...settings };
}

export function validateRepeaterSettings(
  settings: RepeaterSettings,
): string | null {
  const keys = new Set<string>();
  for (const f of settings.itemFields) {
    if (!f.key.trim()) return "REPEATER_ITEM_KEY_REQUIRED";
    if (keys.has(f.key)) return "REPEATER_ITEM_KEY_DUPLICATE";
    keys.add(f.key);
    if (!f.label.trim()) return "REPEATER_ITEM_LABEL_REQUIRED";
    if (f.type === "choice") {
      const opts = f.options ?? [];
      if (
        opts.length === 0 ||
        !opts.some((o) => o.enabled !== false && o.label.trim())
      ) {
        return "REPEATER_ITEM_OPTIONS_REQUIRED";
      }
    }
    if (
      typeof f.min === "number" &&
      typeof f.max === "number" &&
      Number.isFinite(f.min) &&
      Number.isFinite(f.max) &&
      f.min > f.max
    ) {
      return "REPEATER_ITEM_NUMBER_RANGE_INVERTED";
    }
  }

  if (settings.countMode === "fixed") {
    const n = settings.fixedCount;
    if (n === undefined || !Number.isFinite(n) || n < 1) {
      return "REPEATER_FIXED_COUNT_INVALID";
    }
  }

  if (settings.countMode === "range" || settings.countMode === "dynamic") {
    const min = settings.minItems ?? 0;
    const max = settings.maxItems;
    if (!Number.isFinite(min) || min < 0) return "REPEATER_MIN_ITEMS_INVALID";
    if (max === undefined || !Number.isFinite(max) || max < 1) {
      return "REPEATER_MAX_ITEMS_INVALID";
    }
    if (min > max) return "REPEATER_ITEM_COUNT_RANGE_INVERTED";
  }

  return null;
}

export function repeaterEffectiveMinItems(settings: RepeaterSettings): number {
  if (settings.countMode === "fixed") {
    return Math.max(1, settings.fixedCount ?? 1);
  }
  return Math.max(0, settings.minItems ?? 0);
}

export function repeaterEffectiveMaxItems(settings: RepeaterSettings): number {
  if (settings.countMode === "fixed") {
    return Math.max(1, settings.fixedCount ?? 1);
  }
  return Math.max(1, settings.maxItems ?? 1);
}

export function repeaterCanAddRemove(settings: RepeaterSettings): boolean {
  if (settings.countMode === "fixed") return false;
  if (settings.allowAddRemove === false) return false;
  return true;
}

export function formatRepeaterItemLabel(
  template: string | undefined,
  index: number,
): string {
  const tpl = template?.trim() || DEFAULT_ITEM_LABEL;
  return tpl.replaceAll("#{index}", String(index)).replaceAll("{index}", String(index));
}

export function createEmptyRepeaterRow(
  settings: RepeaterSettings,
): RepeaterItemRow {
  const row: RepeaterItemRow = {};
  for (const f of settings.itemFields) {
    row[f.key] = "";
  }
  return row;
}

export function getInitialRepeaterRows(
  settings: RepeaterSettings,
): RepeaterItemRow[] {
  const count =
    settings.countMode === "fixed"
      ? repeaterEffectiveMinItems(settings)
      : Math.max(1, repeaterEffectiveMinItems(settings) || 1);
  return Array.from({ length: count }, () => createEmptyRepeaterRow(settings));
}

export function parseRepeaterAnswer(raw: string): RepeaterAnswer | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const out: RepeaterAnswer = [];
    for (const row of parsed) {
      if (!isRecord(row)) return null;
      const item: RepeaterItemRow = {};
      for (const [k, v] of Object.entries(row)) {
        if (typeof v === "string") item[k] = v;
        else if (v == null) item[k] = "";
        else item[k] = String(v);
      }
      out.push(item);
    }
    return out;
  } catch {
    return null;
  }
}

function validateRepeaterSubFieldValue(
  field: RepeaterItemFieldDef,
  value: string,
): string | null {
  const trimmed = value.trim();
  if (field.required && trimmed.length === 0) {
    return "REQUIRED_MISSING";
  }
  if (trimmed.length === 0) return null;

  if (field.type === "text") {
    if (
      typeof field.minLength === "number" &&
      trimmed.length < field.minLength
    ) {
      return "REPEATER_TEXT_TOO_SHORT";
    }
    if (
      typeof field.maxLength === "number" &&
      trimmed.length > field.maxLength
    ) {
      return "REPEATER_TEXT_TOO_LONG";
    }
    if (field.pattern) {
      try {
        const re = new RegExp(field.pattern);
        if (!re.test(trimmed)) return "REPEATER_PATTERN_MISMATCH";
      } catch {
        return "REPEATER_PATTERN_MISMATCH";
      }
    }
    return null;
  }

  if (field.type === "number") {
    const parsed = parseFormNumberAnswer(trimmed, field.integerOnly === true);
    if (!parsed.ok) {
      return parsed.code === "NUMBER_NOT_INTEGER"
        ? "NUMBER_NOT_INTEGER"
        : "INVALID_NUMBER";
    }
    if (numberAnswerOutOfRange(parsed.n, field.min, field.max)) {
      return "NUMBER_OUT_OF_RANGE";
    }
    return null;
  }

  if (field.type === "date") {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return "INVALID_DATE";
    return null;
  }

  if (field.type === "choice") {
    const allowed = new Set(
      (field.options ?? [])
        .filter((o) => o.enabled !== false)
        .map((o) => o.value),
    );
    if (!allowed.has(trimmed)) return "OPTION_OUT_OF_RANGE";
    return null;
  }

  return null;
}

export type ValidateRepeaterAnswerResult =
  | { ok: true; normalized: string }
  | { ok: false; code: string };

export function validateRepeaterAnswerValue(
  raw: string | null | undefined,
  settings: RepeaterSettings,
  required: boolean,
): ValidateRepeaterAnswerResult {
  const parsed = parseRepeaterAnswer(raw ?? "[]");
  if (!parsed) return { ok: false, code: "REPEATER_INVALID_JSON" };

  const minItems = repeaterEffectiveMinItems(settings);
  const maxItems = repeaterEffectiveMaxItems(settings);

  if (parsed.length === 0) {
    if (required || minItems > 0) return { ok: false, code: "REQUIRED_MISSING" };
    return { ok: true, normalized: "[]" };
  }

  if (parsed.length < minItems) {
    return { ok: false, code: "REPEATER_TOO_FEW_ITEMS" };
  }
  if (parsed.length > maxItems) {
    return { ok: false, code: "REPEATER_TOO_MANY_ITEMS" };
  }

  const fieldMap = new Map(settings.itemFields.map((f) => [f.key, f]));

  for (const row of parsed) {
    for (const f of settings.itemFields) {
      const err = validateRepeaterSubFieldValue(f, row[f.key] ?? "");
      if (err) return { ok: false, code: err };
    }
    for (const key of Object.keys(row)) {
      if (!fieldMap.has(key)) {
        return { ok: false, code: "REPEATER_UNKNOWN_SUBFIELD" };
      }
    }
  }

  const normalizedRows = parsed.map((row) => {
    const out: RepeaterItemRow = {};
    for (const f of settings.itemFields) {
      const v = (row[f.key] ?? "").trim();
      if (f.type === "number" && v.length > 0) {
        const parsedNum = parseFormNumberAnswer(v, f.integerOnly === true);
        out[f.key] = parsedNum.ok ? parsedNum.trimmed : v;
      } else {
        out[f.key] = v;
      }
    }
    return out;
  });

  return { ok: true, normalized: JSON.stringify(normalizedRows) };
}

export function serializeRepeaterAnswer(rows: RepeaterAnswer): string {
  return JSON.stringify(rows);
}

export function getRepeaterClientError(
  rows: RepeaterAnswer,
  settings: RepeaterSettings,
  required: boolean,
): string | null {
  const result = validateRepeaterAnswerValue(
    serializeRepeaterAnswer(rows),
    settings,
    required,
  );
  if (result.ok) return null;
  return repeaterValidationMessage(result.code);
}

export function repeaterValidationMessage(code: string): string {
  switch (code) {
    case "REQUIRED_MISSING":
      return "ກະລຸນາກອກຂໍ້ມູນໃຫ້ຄົບ";
    case "REPEATER_TOO_FEW_ITEMS":
      return "ຈຳນວນລາຍການບໍ່ຄົບຕາມທີ່ກຳນົດ";
    case "REPEATER_TOO_MANY_ITEMS":
      return "ຈຳນວນລາຍການເກີນທີ່ອະນຸຍາດ";
    case "REPEATER_INVALID_JSON":
      return "ຂໍ້ມູນລາຍການບໍ່ຖືກຕ້ອງ";
    case "INVALID_DATE":
      return "ຮູບແບບວັນທີບໍ່ຖືກຕ້ອງ";
    case "INVALID_NUMBER":
      return "ຮູບແບບຕົວເລກບໍ່ຖືກຕ້ອງ";
    case "NUMBER_NOT_INTEGER":
      return "ຮັບສະເພາະຈຳນວນເຕັມເທົ່ານັ້ນ";
    case "NUMBER_OUT_OF_RANGE":
      return "ຄ່າຢູ່ນອກຊ່ວງທີ່ອະນຸຍາດ";
    case "OPTION_OUT_OF_RANGE":
      return "ຄ່າທີ່ເລືອກບໍ່ຢູ່ໃນລາຍການທີ່ອະນຸຍາດ";
    case "REPEATER_PATTERN_MISMATCH":
      return "ຮູບແບບຂໍ້ມູນບໍ່ຖືກຕ້ອງ";
    case "REPEATER_TEXT_TOO_SHORT":
      return "ຂໍ້ຄວາມສັ້ນເກີນໄປ";
    case "REPEATER_TEXT_TOO_LONG":
      return "ຂໍ້ຄວາມຍາວເກີນໄປ";
    default:
      return "ຂໍ້ມູນບໍ່ຖືກຕ້ອງ ກະລຸນາກວດແລ້ວລອງໃໝ່";
  }
}

export function formatRepeaterAnswerDisplay(
  raw: string,
  settings: RepeaterSettings | null,
): string {
  const parsed = parseRepeaterAnswer(raw);
  if (!parsed || parsed.length === 0) return "—";
  if (!settings) return raw;

  return parsed
    .map((row, idx) => {
      const label = formatRepeaterItemLabel(settings.itemLabelTemplate, idx + 1);
      const parts = settings.itemFields
        .map((f) => {
          const v = (row[f.key] ?? "").trim();
          if (!v) return null;
          if (f.type === "choice") {
            const hit = f.options?.find((o) => o.value === v);
            return `${f.label}: ${hit?.label ?? v}`;
          }
          return `${f.label}: ${v}`;
        })
        .filter(Boolean);
      if (parts.length === 0) return `${label}: —`;
      return `${label}: ${parts.join(", ")}`;
    })
    .join("\n");
}
